import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API_BASE = "https://www.smart-lend.com/api";

const normalizeSearchText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const tokenize = (value) =>
  normalizeSearchText(value)
    .split(" ")
    .filter(Boolean);

const buildSearchBlob = (item) =>
  normalizeSearchText(
    [
      item?.question,
      item?.answer,
      item?.category,
      item?.scope,
      item?.scope_label,
      ...(Array.isArray(item?.aliases) ? item.aliases : []),
    ].join(" "),
  );

const categoryTone = {
  assessment: {
    background: "rgba(14, 90, 165, 0.1)",
    color: "#0e5aa5",
  },
  strengths: {
    background: "rgba(17, 121, 73, 0.12)",
    color: "#117949",
  },
  concerns: {
    background: "rgba(188, 71, 73, 0.1)",
    color: "#b42318",
  },
  risk: {
    background: "rgba(196, 123, 13, 0.12)",
    color: "#9c6500",
  },
  repayment: {
    background: "rgba(110, 64, 201, 0.1)",
    color: "#6e40c9",
  },
  liquidity: {
    background: "rgba(31, 95, 143, 0.1)",
    color: "#1f5f8f",
  },
  monthly: {
    background: "rgba(18, 48, 74, 0.08)",
    color: "#12304a",
  },
  account: {
    background: "rgba(9, 124, 152, 0.1)",
    color: "#0b7285",
  },
  "cash-flow": {
    background: "rgba(45, 137, 84, 0.1)",
    color: "#2d8954",
  },
  overview: {
    background: "rgba(18, 48, 74, 0.08)",
    color: "#12304a",
  },
  review: {
    background: "rgba(87, 95, 207, 0.1)",
    color: "#575fcf",
  },
  general: {
    background: "rgba(18, 48, 74, 0.08)",
    color: "#12304a",
  },
};

const getCategoryTone = (category) =>
  categoryTone[category] || categoryTone.general;

const stripLeadingBulletMarker = (line) =>
  line.replace(/^\s*\*(?!\*)\s+/, "");

const renderFormattedAnswer = (answer) => {
  const lines = String(answer || "").split("\n");

  return lines.map((line, index) => {
    const normalizedLine = stripLeadingBulletMarker(line);
    const parts = [];
    const boldRegex = /\*\*(.+?)\*\*/g;
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(normalizedLine)) !== null) {
      if (match.index > lastIndex) {
        parts.push(normalizedLine.slice(lastIndex, match.index));
      }

      parts.push(
        <strong key={`bold-${index}-${match.index}`} style={{ fontWeight: 800 }}>
          {match[1]}
        </strong>,
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < normalizedLine.length) {
      parts.push(normalizedLine.slice(lastIndex));
    }

    return (
      <div
        key={`line-${index}`}
        style={{
          minHeight: normalizedLine.trim() ? "auto" : "0.9em",
          marginTop: index === 0 ? 0 : "0.2em",
        }}
      >
        {parts.length ? parts : null}
      </div>
    );
  });
};

const getStreamingStep = (fullAnswer, currentAnswer) => {
  const remainingText = String(fullAnswer || "").slice(String(currentAnswer || "").length);

  if (!remainingText) {
    return 0;
  }

  if (remainingText.startsWith("\n")) {
    return 1;
  }

  if (/^[.!?:;]/.test(remainingText)) {
    return 1;
  }

  if (remainingText.length > 220) {
    return 6;
  }

  if (remainingText.length > 120) {
    return 5;
  }

  if (remainingText.length > 60) {
    return 4;
  }

  return 3;
};

const formatMessageTime = (value) => {
  if (!value) {
    return "";
  }

  try {
    return new Date(value).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (error) {
    return "";
  }
};

const formatInferenceDuration = (durationMs) => {
  const duration = Number(durationMs);
  if (!Number.isFinite(duration) || duration <= 0) {
    return "";
  }

  if (duration < 1000) {
    return `${Math.round(duration)}ms`;
  }

  return `${(duration / 1000).toFixed(duration >= 10000 ? 0 : 1)}s`;
};

const buildTestWelcomeEntry = (groupId, groupName) => ({
  id: `test-welcome-${groupId}`,
  role: "assistant",
  type: "welcome",
  question: "Bank Statement Assistant",
  receivedAt: new Date().toISOString(),
  answer:
    `Ask a natural-language question about ${groupName || "this group"}. ` +
    "We will send your question together with the group's transactions to the test assistant and only show its answer here.",
});

const interactionToChatEntries = (interaction) => {
  const id = interaction?.id || Date.now();

  return [
    {
      id: `history-user-${id}`,
      role: "user",
      question: interaction?.question || "",
      sentAt: interaction?.created_at,
      interactionId: id,
    },
    {
      id: `history-assistant-${id}`,
      role: "assistant",
      question: "Bank Statement Assistant",
      answer: interaction?.answer || "",
      receivedAt: interaction?.answered_at || interaction?.created_at,
      inferenceDurationMs: interaction?.inference_duration_ms,
      interactionId: id,
    },
  ];
};

const interactionsToChatEntries = (interactions = []) =>
  interactions.flatMap((interaction) => interactionToChatEntries(interaction));

const BankStatementQuestionAssistant = ({
  questionAssistant,
  groupId,
  groupName,
  authHeaders,
  onUnauthorized,
  enabled = false,
  isMobile = false,
}) => {
  const [activeTab, setActiveTab] = useState("prepared");
  const [preparedQuery, setPreparedQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [preparedChatEntries, setPreparedChatEntries] = useState([]);
  const [testQuery, setTestQuery] = useState("");
  const [testChatEntries, setTestChatEntries] = useState([]);
  const [isTestLoading, setIsTestLoading] = useState(false);
  const [testLoadingProgress, setTestLoadingProgress] = useState(0);
  const [isTestHistoryLoading, setIsTestHistoryLoading] = useState(false);
  const [isOlderTestHistoryLoading, setIsOlderTestHistoryLoading] = useState(false);
  const [testHistoryHasMore, setTestHistoryHasMore] = useState(false);
  const [testHistoryOldestId, setTestHistoryOldestId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const threadRef = useRef(null);
  const shouldScrollToBottomRef = useRef(true);
  const historyRequestRef = useRef(0);

  const catalog = questionAssistant?.catalog || [];
  const featured = questionAssistant?.featured || [];

  const assistantVersion = `${groupId || "group"}:${groupName || "group"}:${questionAssistant?.total_questions || 0}:${questionAssistant ? "ready" : "empty"}`;

  const loadTestHistory = useCallback(
    async ({ beforeId = null, prepend = false } = {}) => {
      if (!enabled || !groupId) {
        return;
      }

      const requestId = historyRequestRef.current + 1;
      historyRequestRef.current = requestId;

      if (prepend) {
        setIsOlderTestHistoryLoading(true);
      } else {
        setIsTestHistoryLoading(true);
        shouldScrollToBottomRef.current = true;
      }

      const threadNode = threadRef.current;
      const previousScrollHeight = threadNode?.scrollHeight || 0;

      try {
        const params = new URLSearchParams({ limit: "10" });
        if (beforeId) {
          params.set("before_id", String(beforeId));
        }

        const response = await fetch(
          `${API_BASE}/bank-statement-portal/groups/${groupId}/assistant-test/history?${params.toString()}`,
          { headers: authHeaders || {} },
        );

        if (response.status === 401) {
          onUnauthorized?.();
          return;
        }

        const json = await response.json().catch(() => ({}));
        if (!response.ok || requestId !== historyRequestRef.current) {
          return;
        }

        const interactions = Array.isArray(json?.interactions)
          ? json.interactions
          : [];
        const entries = interactionsToChatEntries(interactions);

        setTestHistoryHasMore(Boolean(json?.has_more));
        setTestHistoryOldestId(json?.oldest_id || null);

        if (prepend) {
          shouldScrollToBottomRef.current = false;
          setTestChatEntries((current) => [...entries, ...current]);
          window.setTimeout(() => {
            if (!threadRef.current) {
              return;
            }

            threadRef.current.scrollTop =
              threadRef.current.scrollHeight - previousScrollHeight;
          }, 0);
          return;
        }

        setTestChatEntries(
          entries.length ? entries : [buildTestWelcomeEntry(groupId, groupName)],
        );
      } finally {
        if (prepend) {
          setIsOlderTestHistoryLoading(false);
        } else {
          setIsTestHistoryLoading(false);
        }
      }
    },
    [authHeaders, enabled, groupId, groupName, onUnauthorized],
  );

  useEffect(() => {
    if (!questionAssistant || !enabled || !groupId) {
      setPreparedChatEntries([]);
      setPreparedQuery("");
      setTestChatEntries([]);
      setTestQuery("");
      setActiveIndex(0);
      setActiveTab("prepared");
      setIsTestLoading(false);
      setTestLoadingProgress(0);
      setIsTestHistoryLoading(false);
      setIsOlderTestHistoryLoading(false);
      setTestHistoryHasMore(false);
      setTestHistoryOldestId(null);
      setIsOpen(false);
      setShowPrompt(false);
      return;
    }

    setPreparedChatEntries([
      {
        id: `prepared-welcome-${groupId}`,
        role: "assistant",
        type: "welcome",
        question: "Prepared banker Q&A",
        receivedAt: new Date().toISOString(),
        answer:
          `Ask about deposits, withdrawals, risk flags, repayments, liquidity, currencies, or specific accounts in ${groupName || "this group"}. ` +
          "Use 1-9 to select a suggestion, arrow keys to move, and Enter to submit.",
      },
    ]);
    setTestChatEntries([]);
    setPreparedQuery("");
    setTestQuery("");
    setActiveIndex(0);
    setActiveTab("prepared");
    setIsTestLoading(false);
    setTestLoadingProgress(0);
    setTestHistoryHasMore(false);
    setTestHistoryOldestId(null);
    setIsOpen(false);
    setShowPrompt(true);
    loadTestHistory();
  }, [assistantVersion, enabled, groupId, groupName, loadTestHistory, questionAssistant]);

  useEffect(() => {
    if (threadRef.current && shouldScrollToBottomRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }

    shouldScrollToBottomRef.current = true;
  }, [activeTab, isOpen, preparedChatEntries, testChatEntries]);

  const handleThreadScroll = useCallback(() => {
    if (
      activeTab !== "test" ||
      !testHistoryHasMore ||
      isTestHistoryLoading ||
      isOlderTestHistoryLoading ||
      !testHistoryOldestId ||
      !threadRef.current
    ) {
      return;
    }

    if (threadRef.current.scrollTop <= 56) {
      loadTestHistory({ beforeId: testHistoryOldestId, prepend: true });
    }
  }, [
    activeTab,
    isOlderTestHistoryLoading,
    isTestHistoryLoading,
    loadTestHistory,
    testHistoryHasMore,
    testHistoryOldestId,
  ]);

  useEffect(() => {
    if (!isTestLoading) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTestLoadingProgress((current) => {
        if (current >= 94) {
          return current;
        }

        if (current < 35) {
          return Math.min(current + 11, 94);
        }

        if (current < 70) {
          return Math.min(current + 7, 94);
        }

        return Math.min(current + 3, 94);
      });
    }, 320);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isTestLoading]);

  useEffect(() => {
    const hasStreamingEntries = testChatEntries.some(
      (entry) => entry.isStreaming && typeof entry.fullAnswer === "string",
    );

    if (!hasStreamingEntries) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTestChatEntries((current) =>
        current.map((entry) => {
          if (!entry.isStreaming || typeof entry.fullAnswer !== "string") {
            return entry;
          }

          const currentAnswer = String(entry.answer || "");
          const step = getStreamingStep(entry.fullAnswer, currentAnswer);
          const nextLength = Math.min(
            currentAnswer.length + step,
            entry.fullAnswer.length,
          );
          const nextAnswer = entry.fullAnswer.slice(0, nextLength);

          if (nextLength >= entry.fullAnswer.length) {
            return {
              ...entry,
              answer: entry.fullAnswer,
              isStreaming: false,
            };
          }

          return {
            ...entry,
            answer: nextAnswer,
          };
        }),
      );
    }, 22);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [testChatEntries]);

  const suggestions = useMemo(() => {
    if (!catalog.length) {
      return [];
    }

    const trimmedQuery = preparedQuery.trim();
    if (!trimmedQuery) {
      return (featured.length ? featured : catalog.slice(0, 9)).slice(0, 9);
    }

    const normalizedQuery = normalizeSearchText(trimmedQuery);
    const queryTokens = tokenize(trimmedQuery);

    return catalog
      .map((item) => {
        const questionText = normalizeSearchText(item.question);
        const aliases = Array.isArray(item.aliases) ? item.aliases : [];
        const searchBlob = buildSearchBlob(item);
        let score = 0;

        if (questionText.startsWith(normalizedQuery)) score += 140;
        else if (questionText.includes(normalizedQuery)) score += 95;

        aliases.forEach((alias) => {
          const normalizedAlias = normalizeSearchText(alias);
          if (!normalizedAlias) return;
          if (normalizedAlias.startsWith(normalizedQuery)) score += 70;
          else if (normalizedAlias.includes(normalizedQuery)) score += 50;
        });

        if (normalizeSearchText(item.scope_label).includes(normalizedQuery)) {
          score += 18;
        }

        queryTokens.forEach((token) => {
          if (!token) return;
          if (questionText.includes(token)) score += 14;
          else if (searchBlob.includes(token)) score += 7;
        });

        return {
          ...item,
          _score: score,
        };
      })
      .filter((item) => item._score > 0)
      .sort((left, right) => {
        if (right._score !== left._score) {
          return right._score - left._score;
        }

        if ((left.priority || 999) !== (right.priority || 999)) {
          return (left.priority || 999) - (right.priority || 999);
        }

        return (left.order || 0) - (right.order || 0);
      })
      .slice(0, 9);
  }, [catalog, featured, preparedQuery]);

  useEffect(() => {
    if (activeIndex > suggestions.length - 1) {
      setActiveIndex(0);
    }
  }, [activeIndex, suggestions]);

  const submitSelection = (item, rawQuery) => {
    if (!item) {
      return;
    }

    const submittedText = rawQuery?.trim() || item.question;
    const timestamp = new Date().toISOString();

    setPreparedChatEntries((current) => [
      ...current,
      {
        id: `user-${Date.now()}-${item.id}`,
        role: "user",
        question: submittedText,
        sentAt: timestamp,
      },
      {
        id: `assistant-${Date.now()}-${item.id}`,
        role: "assistant",
        question: item.question,
        answer: item.answer,
        receivedAt: timestamp,
        category: item.category,
        scope: item.scope,
        scopeLabel: item.scope_label,
      },
    ]);
    setPreparedQuery("");
    setActiveIndex(0);
  };

  const submitFallback = (rawQuery) => {
    const fallbackSuggestions = (featured.length ? featured : catalog.slice(0, 3))
      .slice(0, 3)
      .map((item) => item.question)
      .join(" | ");
    const timestamp = new Date().toISOString();

    setPreparedChatEntries((current) => [
      ...current,
      {
        id: `user-${Date.now()}-fallback`,
        role: "user",
        question: rawQuery,
        sentAt: timestamp,
      },
      {
        id: `assistant-${Date.now()}-fallback`,
        role: "assistant",
        question: "Prepared answer not found",
        receivedAt: timestamp,
        answer: fallbackSuggestions
          ? `No prepared answer matched that wording yet. Try one of these prepared questions: ${fallbackSuggestions}.`
          : "No prepared answers are available yet for this group.",
      },
    ]);
    setPreparedQuery("");
    setActiveIndex(0);
  };

  const handlePreparedSubmit = (event) => {
    event.preventDefault();

    if (!enabled || !questionAssistant) {
      return;
    }

    const trimmedQuery = preparedQuery.trim();
    if (!trimmedQuery) {
      return;
    }

    if (suggestions[activeIndex]) {
      submitSelection(suggestions[activeIndex], trimmedQuery);
      return;
    }

    if (suggestions[0]) {
      submitSelection(suggestions[0], trimmedQuery);
      return;
    }

    submitFallback(trimmedQuery);
  };

  const handlePreparedKeyDown = (event) => {
    if (!enabled || !questionAssistant) {
      return;
    }

    if (event.key === "ArrowDown" && suggestions.length) {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % suggestions.length);
      return;
    }

    if (event.key === "ArrowUp" && suggestions.length) {
      event.preventDefault();
      setActiveIndex((current) =>
        current === 0 ? suggestions.length - 1 : current - 1,
      );
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setPreparedQuery("");
      setActiveIndex(0);
      return;
    }

    if (/^[1-9]$/.test(event.key)) {
      const index = Number(event.key) - 1;
      if (suggestions[index]) {
        event.preventDefault();
        submitSelection(
          suggestions[index],
          preparedQuery.trim() || suggestions[index].question,
        );
      }
    }
  };

  const handleTestSubmit = async (event) => {
    event.preventDefault();

    if (!enabled || !groupId || isTestLoading) {
      return;
    }

    const trimmedQuery = testQuery.trim();
    if (!trimmedQuery) {
      return;
    }

    const timestamp = Date.now();
    const loadingEntryId = `assistant-${timestamp}-test-loading`;
    const requestStartedAt = Date.now();
    const sentAt = new Date(requestStartedAt).toISOString();

    shouldScrollToBottomRef.current = true;
    setTestChatEntries((current) => [
      ...current,
      {
        id: `user-${timestamp}-test`,
        role: "user",
        question: trimmedQuery,
        sentAt,
      },
      {
        id: loadingEntryId,
        role: "assistant",
        answer: "Checking the transaction set with the test assistant...",
        isLoading: true,
        requestStartedAt,
      },
    ]);
    setTestQuery("");
    setIsTestLoading(true);
    setTestLoadingProgress(8);

    try {
      const response = await fetch(
        `${API_BASE}/bank-statement-portal/groups/${groupId}/assistant-test`,
        {
          method: "POST",
          headers: {
            ...(authHeaders || {}),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question: trimmedQuery,
          }),
        },
      );

      if (response.status === 401) {
        const receivedAt = new Date().toISOString();
        const inferenceDurationMs = Date.now() - requestStartedAt;
        setTestChatEntries((current) =>
          current.map((entry) =>
            entry.id === loadingEntryId
              ? {
                  ...entry,
                  answer: "Your session has expired. Please sign in again.",
                  fullAnswer: null,
                  isLoading: false,
                  isError: true,
                  isStreaming: false,
                  receivedAt,
                  inferenceDurationMs,
                }
              : entry,
          ),
        );
        onUnauthorized?.();
        return;
      }

      const json = await response.json().catch(() => ({}));
      const answer = response.ok
        ? String(json?.answer || "").trim() || "No answer was returned."
        : String(json?.message || "Unable to get an answer right now.");
      const receivedAt = new Date().toISOString();
      const interaction = json?.interaction;
      const inferenceDurationMs = Date.now() - requestStartedAt;

      setTestLoadingProgress(100);

      setTestChatEntries((current) =>
        current.map((entry) =>
          entry.id === loadingEntryId
            ? {
                ...entry,
                id: interaction?.id
                  ? `history-assistant-${interaction.id}`
                  : entry.id,
                question: response.ok ? "Bank Statement Assistant" : entry.question,
                answer: response.ok ? "" : answer,
                fullAnswer: response.ok ? answer : null,
                isLoading: false,
                isError: !response.ok,
                isStreaming: response.ok,
                receivedAt: interaction?.answered_at || receivedAt,
                inferenceDurationMs:
                  interaction?.inference_duration_ms || inferenceDurationMs,
                interactionId: interaction?.id,
              }
            : entry,
        ),
      );
    } catch (error) {
      const receivedAt = new Date().toISOString();
      const inferenceDurationMs = Date.now() - requestStartedAt;
      setTestLoadingProgress(100);
      setTestChatEntries((current) =>
        current.map((entry) =>
          entry.id === loadingEntryId
            ? {
                ...entry,
                answer: error?.message || "Unable to reach the test banker assistant.",
                fullAnswer: null,
                isLoading: false,
                isError: true,
                isStreaming: false,
                receivedAt,
                inferenceDurationMs,
              }
            : entry,
        ),
      );
    } finally {
      setIsTestLoading(false);
      window.setTimeout(() => {
        setTestLoadingProgress(0);
      }, 250);
    }
  };

  if (!enabled || !questionAssistant || !groupId) {
    return null;
  }

  const chatEntries = activeTab === "prepared" ? preparedChatEntries : testChatEntries;
  const tabEyebrow = activeTab === "prepared" ? "Prepared Q&A" : "Bank Statement Assistant";
  const tabSubtitle = activeTab === "prepared"
    ? `${questionAssistant.total_questions || 0} prepared questions for ${groupName || "this group"}.`
    : `Send free-form questions for ${groupName || "this group"} to the test assistant using the group's transactions.`;

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleLauncherClick = () => {
    setIsOpen((current) => {
      const nextIsOpen = !current;
      if (nextIsOpen) {
        setShowPrompt(false);
      }

      return nextIsOpen;
    });
  };

  const shellDynamicStyle = {
    ...shellStyle,
    borderRadius: isMobile ? "22px" : shellStyle.borderRadius,
    padding: isMobile ? "12px" : shellStyle.padding,
    width: isMobile
      ? "calc(100vw - 16px)"
      : shellStyle.width,
    maxWidth: isMobile
      ? "calc(100vw - 16px)"
      : shellStyle.maxWidth,
    height: isMobile
      ? "calc(100dvh - 16px)"
      : shellStyle.height,
    maxHeight: isMobile
      ? "calc(100dvh - 16px)"
      : shellStyle.maxHeight,
  };

  const floatingShellDynamicStyle = {
    ...floatingShellStyle,
    top: isMobile ? "8px" : floatingShellStyle.top,
    right: isMobile ? "8px" : floatingShellStyle.right,
    bottom: isMobile ? "8px" : floatingShellStyle.bottom,
    gap: isMobile ? "8px" : floatingShellStyle.gap,
    maxWidth: isMobile
      ? "calc(100vw - 16px)"
      : floatingShellStyle.maxWidth,
    maxHeight: isMobile
      ? "calc(100dvh - 16px)"
      : floatingShellStyle.maxHeight,
  };

  const promptDynamicStyle = {
    ...promptBubbleStyle,
    width: isMobile
      ? "min(260px, calc(100vw - 24px))"
      : promptBubbleStyle.width,
    borderRadius: isMobile ? "20px" : promptBubbleStyle.borderRadius,
    padding: isMobile ? "12px 14px" : promptBubbleStyle.padding,
  };

  const threadDynamicStyle = {
    ...threadStyle,
    marginTop: isMobile ? "12px" : threadStyle.marginTop,
    borderRadius: isMobile ? "18px" : threadStyle.borderRadius,
    padding: isMobile ? "10px" : threadStyle.padding,
    gap: isMobile ? "12px" : threadStyle.gap,
    minHeight: isMobile ? "180px" : threadStyle.minHeight,
  };

  const emptyThreadDynamicStyle = {
    ...emptyThreadStyle,
    padding: isMobile ? "14px" : emptyThreadStyle.padding,
    borderRadius: isMobile ? "14px" : emptyThreadStyle.borderRadius,
    fontSize: isMobile ? "13px" : undefined,
  };

  const messageBubbleDynamicStyle = {
    ...messageBubbleStyle,
    maxWidth: isMobile ? "92%" : messageBubbleStyle.maxWidth,
    borderRadius: isMobile ? "16px" : messageBubbleStyle.borderRadius,
    padding: isMobile ? "12px 14px" : messageBubbleStyle.padding,
  };

  const composerShellDynamicStyle = {
    ...composerShellStyle,
    marginTop: isMobile ? "12px" : composerShellStyle.marginTop,
    borderRadius: isMobile ? "18px" : composerShellStyle.borderRadius,
    padding: isMobile ? "10px" : composerShellStyle.padding,
    height:
      activeTab === "prepared"
        ? (isMobile ? "min(30dvh, 240px)" : composerShellStyle.height)
        : "auto",
    minHeight:
      activeTab === "prepared"
        ? (isMobile ? "190px" : composerShellStyle.minHeight)
        : "unset",
  };

  const suggestionsWrapDynamicStyle = {
    ...suggestionsWrapStyle,
    gap: isMobile ? "8px" : suggestionsWrapStyle.gap,
    paddingRight: isMobile ? "2px" : suggestionsWrapStyle.paddingRight,
  };

  const suggestionCardDynamicStyle = {
    ...suggestionCardStyle,
    borderRadius: isMobile ? "14px" : suggestionCardStyle.borderRadius,
    padding: isMobile ? "10px" : suggestionCardStyle.padding,
  };

  const shortcutDynamicStyle = {
    ...shortcutStyle,
    width: isMobile ? "20px" : shortcutStyle.width,
    height: isMobile ? "20px" : shortcutStyle.height,
    fontSize: isMobile ? "10px" : shortcutStyle.fontSize,
  };

  const composerInputDynamicStyle = {
    ...composerInputStyle,
    borderRadius: isMobile ? "14px" : composerInputStyle.borderRadius,
    padding: isMobile ? "13px 14px" : composerInputStyle.padding,
    fontSize: isMobile ? "14px" : composerInputStyle.fontSize,
  };

  const launcherDynamicStyle = {
    ...launcherButtonStyle,
    width: isMobile ? "58px" : launcherButtonStyle.width,
    height: isMobile ? "58px" : launcherButtonStyle.height,
  };

  const launcherInnerDynamicStyle = {
    ...launcherInnerStyle,
    fontSize: isMobile ? "13px" : undefined,
  };

  const minimizeButtonDynamicStyle = {
    ...minimizeButtonStyle,
    borderRadius: isMobile ? "10px" : minimizeButtonStyle.borderRadius,
    padding: isMobile ? "8px 10px" : minimizeButtonStyle.padding,
    fontSize: isMobile ? "12px" : undefined,
  };

  const tabRailDynamicStyle = {
    ...tabRailStyle,
    marginTop: isMobile ? "12px" : tabRailStyle.marginTop,
    padding: isMobile ? "4px" : tabRailStyle.padding,
    gap: isMobile ? "4px" : tabRailStyle.gap,
  };

  const tabButtonDynamicStyle = {
    ...tabButtonStyle,
    padding: isMobile ? "9px 11px" : tabButtonStyle.padding,
    fontSize: isMobile ? "11px" : tabButtonStyle.fontSize,
  };

  const betaBadgeDynamicStyle = {
    ...betaBadgeStyle,
    fontSize: isMobile ? "10px" : betaBadgeStyle.fontSize,
    padding: isMobile ? "4px 7px" : betaBadgeStyle.padding,
  };

  const sendButtonDynamicStyle = {
    ...sendButtonStyle,
    padding: isMobile ? "12px 14px" : sendButtonStyle.padding,
    fontSize: isMobile ? "12px" : sendButtonStyle.fontSize,
  };

  return (
    <>
      <style>{`
        @keyframes bankerAssistantPromptFloat {
          0% { transform: translateY(10px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes bankerAssistantPulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(14,90,165,0.22); }
          70% { transform: scale(1.03); box-shadow: 0 0 0 18px rgba(14,90,165,0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(14,90,165,0); }
        }
        @keyframes bankerAssistantPing {
          0% { transform: scale(0.88); opacity: 0.85; }
          100% { transform: scale(1.55); opacity: 0; }
        }
        @keyframes bankerAssistantCursorBlink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
      `}</style>

      <div style={floatingShellDynamicStyle}>
        {!isOpen && showPrompt ? (
          <button type="button" onClick={handleLauncherClick} style={promptDynamicStyle}>
            <div style={{ ...eyebrowStyle, fontSize: isMobile ? "11px" : eyebrowStyle.fontSize }}>
              Banker assistant
            </div>
            <div
              style={{
                marginTop: isMobile ? "4px" : "6px",
                fontWeight: 800,
                color: "#12304a",
                fontSize: isMobile ? "14px" : undefined,
              }}
            >
              Any questions about this bank statement group?
            </div>
            <div
              style={{
                marginTop: isMobile ? "5px" : "6px",
                fontSize: isMobile ? "12px" : "13px",
                lineHeight: isMobile ? 1.45 : undefined,
              color: "#617487",
            }}
          >
              {isMobile
                ? "Open prepared Q&A or try the live test assistant for this group."
                : "Open prepared Q&A or try the live test assistant using this group's transactions."}
            </div>
          </button>
        ) : null}

        {isOpen ? (
          <div style={shellDynamicStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                alignItems: "flex-start",
              }}
            >
              <div>
                <div style={{ ...eyebrowStyle, fontSize: isMobile ? "11px" : eyebrowStyle.fontSize }}>
                  {tabEyebrow}
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "18px" : "22px",
                    fontWeight: 900,
                    color: "#12304a",
                  }}
                >
                  Banker question assistant
                </div>
                <div
                  style={{
                    marginTop: isMobile ? "4px" : "6px",
                    color: "#617487",
                    fontSize: isMobile ? "12px" : "13px",
                    lineHeight: isMobile ? 1.45 : undefined,
                  }}
                >
                  {tabSubtitle}
                </div>
                {activeTab === "test" ? (
                  <div
                    style={{
                      marginTop: isMobile ? "8px" : "10px",
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={betaBadgeDynamicStyle}>Beta feature</span>
                    <span
                      style={{
                        color: "#617487",
                        fontSize: isMobile ? "11px" : "12px",
                        lineHeight: 1.45,
                      }}
                    >
                      Answers are experimental and may change as we refine this assistant.
                    </span>
                  </div>
                ) : null}
                <div style={tabRailDynamicStyle}>
                  <button
                    type="button"
                    onClick={() => setActiveTab("prepared")}
                    style={{
                      ...tabButtonDynamicStyle,
                      ...(activeTab === "prepared" ? activeTabButtonStyle : {}),
                    }}
                  >
                    Prepared Q&A
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("test")}
                    style={{
                      ...tabButtonDynamicStyle,
                      ...(activeTab === "test" ? activeTabButtonStyle : {}),
                    }}
                  >
                    Bank Statement Assistant Beta
                  </button>
                </div>
              </div>
              <button type="button" onClick={handleClose} style={minimizeButtonDynamicStyle}>
                Minimize
              </button>
            </div>

            <div ref={threadRef} onScroll={handleThreadScroll} style={threadDynamicStyle}>
              {activeTab === "test" && isOlderTestHistoryLoading ? (
                <div style={historyStatusStyle}>Loading older chat...</div>
              ) : null}
              {activeTab === "test" && testHistoryHasMore && !isOlderTestHistoryLoading ? (
                <div style={historyStatusStyle}>Scroll up to load older chat</div>
              ) : null}
              {!chatEntries.length || (activeTab === "test" && isTestHistoryLoading) ? (
                <div style={emptyThreadDynamicStyle}>
                  {activeTab === "test" && isTestHistoryLoading
                    ? "Loading recent assistant conversation..."
                    : activeTab === "prepared"
                    ? "Start typing a banker-style question to see prepared matches."
                    : "Ask a free-form question and the test assistant will answer from the group's transactions."}
                </div>
              ) : (
                chatEntries.map((entry) => {
                  const tone = getCategoryTone(entry.category);
                  const isUser = entry.role === "user";
                  const messageTimeLabel = isUser
                    ? formatMessageTime(entry.sentAt)
                    : formatMessageTime(entry.receivedAt);
                  const inferenceLabel = !isUser
                    ? formatInferenceDuration(entry.inferenceDurationMs)
                    : "";

                  return (
                    <div
                      key={entry.id}
                      style={{
                        display: "flex",
                        justifyContent: isUser ? "flex-end" : "flex-start",
                      }}
                    >
                      <div
                        style={{
                          ...messageBubbleDynamicStyle,
                          ...(isUser ? userBubbleStyle : assistantBubbleStyle),
                        }}
                      >
                        {entry.category ? (
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            <span style={{ ...tagStyle, ...tone }}>{entry.category}</span>
                            {entry.scopeLabel ? (
                              <span style={secondaryTagStyle}>{entry.scopeLabel}</span>
                            ) : null}
                          </div>
                        ) : null}

                        {entry.question ? (
                          <div
                            style={{
                              marginTop: entry.category ? (isMobile ? "8px" : "10px") : 0,
                              fontWeight: isUser ? 700 : 800,
                              color: isUser ? "#fff" : "#12304a",
                              lineHeight: 1.5,
                              fontSize: isMobile ? "13px" : undefined,
                            }}
                          >
                            {entry.question}
                          </div>
                        ) : null}

                        {entry.answer ? (
                          <div
                            style={{
                              marginTop: entry.question ? (isMobile ? "8px" : "10px") : 0,
                              color: isUser ? "#fff" : entry.isError ? "#b42318" : "#33536d",
                              lineHeight: 1.65,
                              fontSize: isMobile ? "13px" : "14px",
                              opacity: entry.isLoading ? 0.72 : 1,
                            }}
                          >
                            {renderFormattedAnswer(entry.answer)}
                            {entry.isStreaming ? (
                              <span style={streamingCursorStyle}>|</span>
                            ) : null}
                          </div>
                        ) : null}

                        {entry.isLoading ? (
                          <div style={{ marginTop: entry.answer ? "12px" : 0 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: "10px",
                                alignItems: "center",
                                color: "#617487",
                                fontSize: isMobile ? "11px" : "12px",
                                fontWeight: 700,
                              }}
                            >
                              <span>Loading progress</span>
                              <span>{testLoadingProgress}%</span>
                            </div>
                            <div
                              style={{
                                marginTop: "8px",
                                height: "8px",
                                borderRadius: "999px",
                                background: "rgba(18,48,74,0.08)",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${testLoadingProgress}%`,
                                  minWidth: testLoadingProgress ? "10px" : 0,
                                  borderRadius: "999px",
                                  background:
                                    "linear-gradient(90deg, #0e5aa5 0%, #8cc2ff 50%, #0e5aa5 100%)",
                                  backgroundSize: "140px 100%",
                                  animation: "portalProgressShift 1.5s linear infinite",
                                  transition: "width 240ms ease",
                                }}
                              />
                            </div>
                          </div>
                        ) : null}

                        {messageTimeLabel || inferenceLabel ? (
                          <div
                            style={{
                              marginTop: "10px",
                              display: "flex",
                              justifyContent: isUser ? "flex-end" : "space-between",
                              gap: "10px",
                              alignItems: "center",
                              flexWrap: "wrap",
                              fontSize: isMobile ? "10px" : "11px",
                              color: isUser ? "rgba(255,255,255,0.82)" : "#7a8ea4",
                            }}
                          >
                            <span>
                              {isUser ? "Sent" : "Received"} {messageTimeLabel}
                            </span>
                            {!isUser && inferenceLabel ? (
                              <span>Inference took {inferenceLabel}</span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div style={composerShellDynamicStyle}>
              <div
                style={{
                  color: "#617487",
                  fontSize: isMobile ? "11px" : "12px",
                  marginBottom: isMobile ? "8px" : "10px",
                }}
              >
                {activeTab === "prepared"
                  ? "`1-9` select • `↑ ↓` move • `Enter` submit"
                  : "Free-form question mode • only the returned answer is shown"}
              </div>

              {activeTab === "prepared" ? (
                <>
                  <div style={suggestionsWrapDynamicStyle}>
                    {(suggestions.length ? suggestions : []).map((item, index) => {
                      const isActive = index === activeIndex;
                      const tone = getCategoryTone(item.category);

                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() =>
                            submitSelection(item, preparedQuery.trim() || item.question)
                          }
                          onMouseEnter={() => setActiveIndex(index)}
                          style={{
                            ...suggestionCardDynamicStyle,
                            border: isActive
                              ? "1px solid rgba(14, 90, 165, 0.38)"
                              : "1px solid rgba(18,48,74,0.08)",
                            background: isActive ? "#eef6ff" : "#fff",
                            boxShadow: isActive
                              ? "0 8px 18px rgba(14,90,165,0.12)"
                              : "none",
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <span style={shortcutDynamicStyle}>{index + 1}</span>
                              <span
                                style={{
                                  ...tagStyle,
                                  ...tone,
                                  padding: isMobile ? "5px 8px" : tagStyle.padding,
                                  fontSize: isMobile ? "10px" : tagStyle.fontSize,
                                }}
                              >
                                {item.category}
                              </span>
                            </div>
                            <div
                              style={{
                                marginTop: isMobile ? "8px" : "10px",
                                fontWeight: 700,
                                color: "#12304a",
                                lineHeight: 1.45,
                                textAlign: "left",
                                fontSize: isMobile ? "12px" : "13px",
                              }}
                            >
                              {item.question}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <form
                    onSubmit={handlePreparedSubmit}
                    style={{ marginTop: isMobile ? "10px" : "14px" }}
                  >
                    <input
                      value={preparedQuery}
                      onChange={(event) => {
                        setPreparedQuery(event.target.value);
                        setActiveIndex(0);
                      }}
                      onKeyDown={handlePreparedKeyDown}
                      placeholder="Ask about totals, risk flags, repayments, liquidity..."
                      style={composerInputDynamicStyle}
                    />
                  </form>
                </>
              ) : (
                <form onSubmit={handleTestSubmit} style={{ marginTop: isMobile ? "10px" : "14px" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: isMobile ? "8px" : "10px",
                      alignItems: "stretch",
                    }}
                  >
                    <input
                      value={testQuery}
                      onChange={(event) => setTestQuery(event.target.value)}
                      placeholder="Ask anything about spending, deposits, merchants, or trends..."
                      style={composerInputDynamicStyle}
                      disabled={isTestLoading}
                    />
                    <button
                      type="submit"
                      style={sendButtonDynamicStyle}
                      disabled={isTestLoading}
                    >
                      {isTestLoading ? "Asking..." : "Send"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        ) : null}

        <button type="button" onClick={handleLauncherClick} style={launcherDynamicStyle}>
          <span style={launcherPingStyle} />
          <span style={launcherInnerDynamicStyle}>Ask</span>
        </button>
      </div>
    </>
  );
};

const shellStyle = {
  borderRadius: "28px",
  border: "1px solid rgba(18,48,74,0.08)",
  background:
    "radial-gradient(circle at top right, rgba(14,90,165,0.09), transparent 26%), linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,250,255,0.98) 100%)",
  padding: "18px",
  width: "min(max(380px, 30vw), calc(100vw - 24px))",
  maxWidth: "calc(100vw - 24px)",
  height: "calc(100dvh - 24px)",
  maxHeight: "calc(100dvh - 24px)",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  boxShadow: "0 24px 50px rgba(18,48,74,0.2)",
  pointerEvents: "auto",
};

const eyebrowStyle = {
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#0e5aa5",
};

const tabRailStyle = {
  marginTop: "14px",
  display: "inline-flex",
  gap: "6px",
  padding: "5px",
  borderRadius: "999px",
  background: "rgba(18,48,74,0.06)",
  border: "1px solid rgba(18,48,74,0.08)",
};

const tabButtonStyle = {
  border: "none",
  background: "transparent",
  color: "#486179",
  fontSize: "12px",
  fontWeight: 800,
  borderRadius: "999px",
  padding: "10px 12px",
};

const activeTabButtonStyle = {
  background: "#ffffff",
  color: "#12304a",
  boxShadow: "0 8px 18px rgba(18,48,74,0.08)",
};

const betaBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: "999px",
  padding: "5px 9px",
  background: "rgba(196, 123, 13, 0.14)",
  color: "#9c6500",
  fontSize: "11px",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const threadStyle = {
  marginTop: "16px",
  borderRadius: "24px",
  background: "#fff",
  border: "1px solid rgba(18,48,74,0.08)",
  padding: "14px",
  display: "grid",
  gap: "14px",
  flex: "1 1 auto",
  minHeight: "220px",
  overflowY: "auto",
};

const emptyThreadStyle = {
  padding: "18px",
  borderRadius: "18px",
  background: "#f8fafc",
  color: "#617487",
  border: "1px dashed rgba(18,48,74,0.12)",
};

const historyStatusStyle = {
  justifySelf: "center",
  padding: "7px 11px",
  borderRadius: "999px",
  background: "rgba(18,48,74,0.06)",
  color: "#617487",
  fontSize: "11px",
  fontWeight: 800,
};

const messageBubbleStyle = {
  maxWidth: "88%",
  borderRadius: "20px",
  padding: "16px 18px",
};

const userBubbleStyle = {
  background: "linear-gradient(135deg, #0e5aa5 0%, #2f82d1 100%)",
  color: "#fff",
  borderBottomRightRadius: "8px",
};

const assistantBubbleStyle = {
  background: "#f7fbff",
  border: "1px solid rgba(14,90,165,0.12)",
  borderBottomLeftRadius: "8px",
};

const tagStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: "999px",
  padding: "6px 10px",
  fontSize: "11px",
  fontWeight: 800,
  textTransform: "capitalize",
};

const secondaryTagStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: "999px",
  padding: "6px 10px",
  fontSize: "11px",
  fontWeight: 700,
  background: "rgba(18,48,74,0.06)",
  color: "#486179",
};

const composerShellStyle = {
  marginTop: "14px",
  borderRadius: "22px",
  background: "#fffdf8",
  border: "1px solid rgba(18,48,74,0.08)",
  padding: "14px",
  flex: "0 0 auto",
  height: "min(34dvh, 360px)",
  minHeight: "260px",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
};

const suggestionsWrapStyle = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "10px",
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  alignContent: "start",
  paddingRight: "4px",
};

const suggestionCardStyle = {
  borderRadius: "18px",
  padding: "14px",
  textAlign: "left",
  width: "100%",
};

const shortcutStyle = {
  width: "22px",
  height: "22px",
  borderRadius: "999px",
  background: "#12304a",
  color: "#fff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: 900,
};

const composerInputStyle = {
  flex: 1,
  width: "100%",
  borderRadius: "16px",
  border: "1px solid rgba(18,48,74,0.12)",
  padding: "16px 18px",
  outline: "none",
  fontSize: "15px",
  color: "#12304a",
  background: "#fff",
};

const sendButtonStyle = {
  border: "none",
  borderRadius: "16px",
  padding: "14px 18px",
  background: "#12304a",
  color: "#fff",
  fontSize: "13px",
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const streamingCursorStyle = {
  display: "inline-block",
  marginLeft: "2px",
  color: "#0e5aa5",
  fontWeight: 800,
  animation: "bankerAssistantCursorBlink 1s step-end infinite",
};

const floatingShellStyle = {
  position: "fixed",
  top: "12px",
  right: "12px",
  bottom: "12px",
  zIndex: 1200,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  justifyContent: "flex-end",
  gap: "12px",
  maxWidth: "calc(100vw - 24px)",
  maxHeight: "calc(100dvh - 24px)",
  pointerEvents: "none",
};

const promptBubbleStyle = {
  width: "min(320px, calc(100vw - 36px))",
  border: "1px solid rgba(14,90,165,0.16)",
  borderRadius: "24px",
  background: "rgba(255,255,255,0.98)",
  padding: "16px 18px",
  textAlign: "left",
  boxShadow: "0 18px 36px rgba(18,48,74,0.14)",
  animation: "bankerAssistantPromptFloat 320ms ease-out",
  pointerEvents: "auto",
};

const launcherButtonStyle = {
  position: "relative",
  width: "68px",
  height: "68px",
  borderRadius: "999px",
  border: "none",
  background: "linear-gradient(135deg, #0e5aa5 0%, #2d83d4 100%)",
  color: "#fff",
  fontWeight: 900,
  boxShadow: "0 16px 32px rgba(14,90,165,0.28)",
  animation: "bankerAssistantPulse 2.4s ease-out infinite",
  overflow: "visible",
  flex: "0 0 auto",
  pointerEvents: "auto",
};

const launcherPingStyle = {
  position: "absolute",
  inset: 0,
  borderRadius: "999px",
  border: "2px solid rgba(14,90,165,0.28)",
  animation: "bankerAssistantPing 1.9s ease-out infinite",
};

const launcherInnerStyle = {
  position: "relative",
  zIndex: 1,
};

const minimizeButtonStyle = {
  border: "1px solid rgba(18,48,74,0.12)",
  borderRadius: "12px",
  padding: "10px 12px",
  background: "#fff",
  color: "#12304a",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

export default BankStatementQuestionAssistant;
